import { useState, useCallback, useEffect } from 'react';
import { useSearchParams, useRouter } from 'next/navigation';
import Tabs from '@carvana/ds-tabs';
import Skeleton from '@carvana/ds-skeleton';
import { datadogRum } from '@datadog/browser-rum';
import styles from '../HeroLoadingFallback.module.css';
import PlateEntry from './PlateEntry';
import VinEntry from './VinEntry';
import {
  Container,
  StyledButton,
  StyledTab,
  StyledTabBar,
  StyledTabPanel,
  TabContainer
} from './Entry.styles';
import { PlateState, PlateTextWrapper, Wrapper } from './PlateEntry/PlateEntry.styles';
import { getInvalidVINLetter, isVinValid } from './Entry.helpers';
import useAnalyticEvent from 'shared/hooks/useAnalyticEvent';
import { EventActions } from 'analytics/uiMetrics';
import { buildNavigationUrl } from 'shared/routes';
import { APP_BASE_PATH } from 'shared/appBasePath';

const wizardRoute = `${APP_BASE_PATH}/getoffer/vehicle`;

const lettersToNumbers = {
  I: '1',
  O: '0',
  Q: '9'
};

const vinIncorrectLetterError = (letter: string) =>
  `The letter '${letter}' is invalid. Try the number '${
    lettersToNumbers[letter as keyof typeof lettersToNumbers]
  }' instead.`;
const requiredFieldMessage = 'This field is required.';

const VehicleEntryContainer = () => {
  const [hasMounted, setHasMounted] = useState(false);
  const router = useRouter();
  const searchParams = useSearchParams();
  const prefillVin = searchParams.get('vin') || '';
  const prefillPlate = searchParams.get('licensePlate') || '';
  const prefillPlateState = searchParams.get('plateState') || '';
  const [plate, setPlate] = useState(prefillPlate);
  const [plateState, setPlateState] = useState(prefillPlateState);
  const [vin, setVin] = useState(prefillVin);
  const [incorrectLetter, setIncorrectLetter] = useState(getInvalidVINLetter(vin) ?? '');
  const [activeTab, setActiveTab] = useState(prefillVin ? 'Vin' : 'PlateState');
  const [vinTouched, setVinTouched] = useState(false);
  const [plateTouched, setPlateTouched] = useState(false);
  const { recordActionClicked, recordGenericEvent } = useAnalyticEvent();

  useEffect(() => {
    setHasMounted(true);
  }, []);

  const getRequiredVinError = () => {
    if (!vin) {
      return requiredFieldMessage;
    }
    if (incorrectLetter) {
      return vinIncorrectLetterError(incorrectLetter);
    }
    if (vin.length !== 17) {
      return 'Invalid VIN.';
    }
    return '';
  };

  const getPlateStateError = () => {
    if (!plate) {
      return requiredFieldMessage;
    }
    return '';
  };

  const plateStateErrorToDisplay = !plateState ? requiredFieldMessage : '';

  const handleTabChange = (tabName: string) => {
    setActiveTab(tabName);
    recordActionClicked(
      'Landing Page VIN or Plate Toggle Bar',
      'VehicleEntry Widget',
      {
        selectedTab: tabName
      },
      true
    );
  };

  const updatePlate = ({ target }: React.ChangeEvent<HTMLInputElement>) => {
    let { value } = target;

    if (value) {
      value = value?.toUpperCase().slice(0, 8);
    }

    setPlate(value);
    setPlateTouched(false);
  };

  const updatePlateState = ({ value }: { value: string }) => {
    setPlateState(value);
  };

  const updateVin = ({ target }: React.ChangeEvent<HTMLInputElement>) => {
    let { value } = target;

    if (value) {
      const invalidLetter = getInvalidVINLetter(value);
      setIncorrectLetter(invalidLetter ?? '');
      value = value
        .replace(/[^A-Z0-9]/gi, '')
        ?.toUpperCase()
        .slice(0, 17);
    } else {
      setIncorrectLetter('');
    }

    setVin(value);
    setVinTouched(false);
  };

  const handleSubmitEvent = useCallback(
    (submissionData?: { vin: string } | { plate: string; plateState: string }) => {
      const isVinSubmission = submissionData && 'vin' in submissionData;
      const payload = submissionData;
      const submissionType = isVinSubmission ? 'Vin' : 'Plate/State';

      const genericEvent = recordGenericEvent(
        `LandingPage - ${submissionType} submission`,
        'Get My Offer',
        EventActions.Clicked,
        payload,
        true
      );
      const actionClickedEvent = recordActionClicked('Landing Page', 'Get My Offer', payload, true);
      return Promise.all([genericEvent, actionClickedEvent]);
    },
    [recordActionClicked, recordGenericEvent]
  );

  const handleSubmit = () => {
    const isPlateTab = activeTab === 'PlateState';
    const validVin = !isPlateTab && isVinValid(vin);
    const validPlateState = isPlateTab && !!plate?.length && !!plateState?.length;

    if (isPlateTab) {
      setPlateTouched(true);
    } else {
      setVinTouched(true);
    }

    if (validPlateState) {
      const submissionData = { plate, plateState };
      handleSubmitEvent(submissionData);
      const paramsToAdd = {
        licensePlate: submissionData.plate,
        plateState: submissionData.plateState
      };
      const redirectUrl = buildNavigationUrl({ baseUrl: wizardRoute, paramsToAdd });
      router.push(redirectUrl);
    } else if (validVin) {
      const submissionData = { vin };
      handleSubmitEvent(submissionData);
      datadogRum.setGlobalContextProperty?.('vin', vin);
      const paramsToAdd = { vin: submissionData.vin };
      const redirectUrl = buildNavigationUrl({ baseUrl: wizardRoute, paramsToAdd });
      router.push(redirectUrl);
    }
  };

  const isPlateTab = activeTab === 'PlateState';
  const hasPlateErrors = !plate?.length || !plateState?.length;
  const hasVinErrors = !vin?.length || vin.length !== 17 || !!incorrectLetter;
  const isButtonDisabled = isPlateTab ? plateTouched && hasPlateErrors : vinTouched && hasVinErrors;

  return (
    <Container>
      <TabContainer>
        {hasMounted ? (
          <Tabs activeTab={activeTab} onTabChange={handleTabChange}>
            <StyledTabBar className="vehicle-entry">
              <StyledTab className="vehicle-entry" tabGroup="PlateState">
                License Plate
              </StyledTab>
              <StyledTab className="vehicle-entry" tabGroup="Vin">
                VIN
              </StyledTab>
            </StyledTabBar>
            <StyledTabPanel tabGroup="PlateState">
              <PlateEntry
                plateError={getPlateStateError()}
                plateStateError={plateStateErrorToDisplay}
                plate={plate}
                plateState={plateState}
                touched={plateTouched}
                updatePlate={updatePlate}
                updatePlateState={updatePlateState}
              />
            </StyledTabPanel>
            <StyledTabPanel tabGroup="Vin">
              <VinEntry
                error={getRequiredVinError()}
                updateVin={updateVin}
                vin={vin}
                touched={vinTouched}
              />
            </StyledTabPanel>
          </Tabs>
        ) : (
          <>
            <StyledTabBar className="vehicle-entry" as="div">
              <StyledTab className={`vehicle-entry ${styles.relative}`} as="div">
                <span className={styles.hidden} aria-hidden="true">License Plate</span>
                <div className={`${styles.absoluteOverlay} ${styles.flexCenter}`}>
                  <Skeleton width="80%" height="14px" />
                </div>
              </StyledTab>
              <StyledTab className={`vehicle-entry ${styles.relative}`} as="div">
                <span className={styles.hidden} aria-hidden="true">VIN</span>
                <div className={`${styles.absoluteOverlay} ${styles.flexCenter}`}>
                  <Skeleton width="80%" height="14px" />
                </div>
              </StyledTab>
            </StyledTabBar>
            <PlateState>
              <PlateTextWrapper>
                <Skeleton width="100%" height="48px" />
              </PlateTextWrapper>
              <Wrapper>
                <Skeleton width="100%" height="48px" />
              </Wrapper>
            </PlateState>
          </>
        )}
      </TabContainer>
      {hasMounted ? (
        <StyledButton variant="primary" onClick={handleSubmit} disabled={isButtonDisabled}>
          Get Your Offer
        </StyledButton>
      ) : (
        <StyledButton as="div" className={styles.buttonContainer}>
          <span className={styles.hidden} aria-hidden="true">Get your offer</span>
          <div className={`${styles.absoluteOverlay} ${styles.flexCenter}`}>
            <Skeleton width="100%" height="100%" />
          </div>
        </StyledButton>
      )}
    </Container>
  );
};

export default VehicleEntryContainer;
